import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * La tarea que el trabajador tiene EN MARCHA, si tiene alguna.
 *
 * Por qué existe: una tarea empezada y nunca terminada no la arregla nadie —el
 * trabajador se va a otra pantalla, se le olvida, y el parte se queda a medias—.
 * Así que mientras haya una en marcha, el portal del trabajador se queda en ella
 * y no deja ir a ningún otro sitio. Quien decide es el SERVIDOR, no el aparato:
 * el candado es `planner_tasks.status = 'in_progress'`, así que cerrar la app,
 * quedarse sin batería o cambiar de teléfono da exactamente el mismo resultado.
 * No hay nada guardado en el móvil que se pueda desincronizar.
 *
 * Es a propósito que cuente también las que puso en marcha el responsable desde
 * el planner: para el trabajador «esta tarea está empezada» significa lo mismo
 * venga de donde venga.
 *
 * Solo para el rol `worker`; el resto de roles pasan `enabled: false` y esto no
 * llega a consultar nada.
 */
export interface ActiveWorkerTask {
  id: string;
  title: string;
  actual_start_time: string | null;
}

export function useActiveWorkerTask(enabled: boolean) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["worker-active-task", user?.id ?? null],
    enabled: enabled && !!user?.id,
    // Corto y con revalidación al volver a la app: si el trabajador termina la
    // tarea en otro sitio —o el responsable la detiene por él—, el candado tiene
    // que soltarse solo, sin recargar.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ActiveWorkerTask | null> => {
      const { data, error } = await supabase
        .from("planner_tasks")
        .select("id, title, actual_start_time")
        .eq("assigned_to", user!.id)
        .eq("status", "in_progress")
        // Si por lo que sea hubiera más de una en marcha (el responsable puede
        // marcarlas desde el planner), manda la más reciente. Elegir siempre la
        // misma es lo que impide que el trabajador rebote entre dos.
        .order("actual_start_time", { ascending: false, nullsFirst: false })
        .limit(1);

      if (error) throw error;
      return data?.[0] ?? null;
    },
  });

  return {
    activeTask: query.data ?? null,
    /** Mientras no se sabe, NO se encierra a nadie: ver `WorkerTaskLock`. */
    resolved: enabled ? query.isSuccess || query.isError : true,
    refetch: query.refetch,
  };
}

/** La clave, para invalidarla al empezar, detener o completar una tarea. */
export const ACTIVE_WORKER_TASK_KEY = (userId: string | null | undefined) => [
  "worker-active-task",
  userId ?? null,
];
